import { calculateSalesLineBenefit } from '@erp/domain/sales';
import type {
  RepresentativeStats,
  StatsDocument,
  StatsDocumentLine,
  StatsPeriod,
} from './types';

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// Bénéfice ligne = source unique @erp/domain/sales. `purchasePrice` est le coût
// net HT figé sur la ligne ; une ligne sans coût connu rapporte 0 (pas de
// surévaluation de la commission).
function lineProfit(line: StatsDocumentLine): number {
  return calculateSalesLineBenefit({
    quantity: line.quantity,
    unitPriceHt: line.unitPriceHt,
    discountPct: line.discountPct,
    unitCostHt: line.purchasePrice,
  }).benefit;
}

function lineRevenue(line: StatsDocumentLine): number {
  const qty = Number(line.quantity) || 0;
  const sell = Number(line.unitPriceHt) || 0;
  const disc = Number(line.discountPct) || 0;
  return sell * (1 - disc / 100) * qty;
}

function isInPeriod(date: string, period?: StatsPeriod): boolean {
  if (!period) return true;
  if (period.from && date < period.from) return false;
  if (period.to && date > period.to) return false;
  return true;
}

/**
 * Détermine si un document "compte" dans les stats réalisées.
 *
 * Règles validées avec le client :
 * - factures : toujours comptées en positif
 * - BL non facturés : comptés en positif (vente réalisée pas encore facturée)
 * - BL déjà facturés : ignorés (évite le double comptage avec leur facture)
 * - avoirs : comptés en négatif
 * - devis / commandes : ignorés (pas encore réalisés)
 */
function documentContribution(doc: StatsDocument): 'positive' | 'negative' | 'none' {
  if (doc.docType === 'invoice') return 'positive';
  if (doc.docType === 'delivery') return doc.isInvoiced ? 'none' : 'positive';
  if (doc.docType === 'credit_note') return 'negative';
  return 'none';
}

/**
 * Calcule CA HT + bénéfice pour un représentant sur une période.
 *
 * `documents` doit être pré-filtré sur le représentant concerné. Cette
 * fonction se contente de classifier les types de documents et d'appliquer
 * le filtre de période.
 */
export function calculateRepresentativeStats(
  documents: StatsDocument[],
  period?: StatsPeriod,
): RepresentativeStats {
  let revenue = 0;
  let profit = 0;
  let docCount = 0;

  for (const doc of documents) {
    if (!isInPeriod(doc.docDate, period)) continue;
    const contrib = documentContribution(doc);
    if (contrib === 'none') continue;

    const sign = contrib === 'positive' ? 1 : -1;
    const docRevenue = doc.lines.reduce((s, l) => s + lineRevenue(l), 0);
    const docProfit = doc.lines.reduce((s, l) => s + lineProfit(l), 0);

    revenue += sign * docRevenue;
    profit += sign * docProfit;
    docCount += 1;
  }

  return {
    revenue: round3(revenue),
    profit: round3(profit),
    docCount,
  };
}
